import { useState, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/_generated/api';
import type { Id } from '@convex/_generated/dataModel';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertCircle,
  Save,
  ChevronLeft,
  ChevronRight,
  X,
  User,
  Mail,
  ShieldCheck,
  Megaphone,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { StepProgress } from '@/components/insurance/StepProgress';
import { FieldRenderer } from './FieldRenderer';
import { ReviewStep } from './ReviewStep';
import { PolicyInformationCard } from '@/components/insurance/PolicyInformationCard';
import { PolicyStatusCard } from '@/components/insurance/PolicyStatusCard';
import { usePolicyApplicationWizard } from '@/hooks/usePolicyApplicationWizard';
import type {
  IPolicyApplicationWizardProps,
  IReviewSectionItem,
  IRepeatableGroupEntry,
  IConditionalDetailValue,
  IFormStep,
  IFormField,
} from '@/@types/policyFormTemplates';

const POLICY_SETUP_STEP = 0;

const SECTION_ICONS: Record<string, React.ReactNode> = {
  user: <User className="h-4 w-4" />,
  mail: <Mail className="h-4 w-4" />,
  'shield-check': <ShieldCheck className="h-4 w-4" />,
  megaphone: <Megaphone className="h-4 w-4" />,
};

const GRID_COLS_CLASS: Record<number, string> = {
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
};

function groupFieldsBySections(fields: IFormField[]): Array<{
  name: string | null;
  icon: string | null;
  columns: number;
  description: string | null;
  fields: IFormField[];
}> {
  const sections: Array<{
    name: string | null;
    icon: string | null;
    columns: number;
    description: string | null;
    fields: IFormField[];
  }> = [];
  let currentSection: string | null = null;

  for (const field of fields) {
    const sectionName = field.section ?? null;
    if (sectionName !== currentSection || sections.length === 0) {
      sections.push({
        name: sectionName,
        icon: field.sectionIcon ?? null,
        columns: field.sectionColumns ?? 2,
        description: field.sectionDescription ?? null,
        fields: [field],
      });
      currentSection = sectionName;
    } else {
      sections[sections.length - 1].fields.push(field);
    }
  }

  return sections;
}

function isFieldVisible(field: IFormField, values: Record<string, unknown>): boolean {
  if (!field.conditionalOn) return true;
  return values[field.conditionalOn.fieldKey] === field.conditionalOn.value;
}

function isRequiredFieldMissing(field: IFormField, value: unknown): boolean {
  if (!field.required) return false;
  switch (field.fieldType) {
    case 'text':
    case 'email':
    case 'phone':
    case 'textarea':
    case 'select':
    case 'date':
      return !value || String(value).trim() === '';
    case 'number':
      return value === undefined || value === null || value === '';
    case 'boolean':
    case 'checkbox':
      return value === undefined || value === null;
    case 'address': {
      if (!value || typeof value !== 'object') return true;
      const a = value as Record<string, unknown>; // AS_JUSTIFIED: narrowed by object check; only reading well-known string fields
      return (
        !String(a['street'] ?? '').trim() ||
        !String(a['city'] ?? '').trim() ||
        !String(a['postalCode'] ?? '').trim()
      );
    }
    case 'conditional_detail': {
      if (!value || typeof value !== 'object') return true;
      const cd = value as Record<string, unknown>; // AS_JUSTIFIED: narrowed by object check; reading 'answer' key only
      return cd['answer'] === undefined || cd['answer'] === null;
    }
    case 'repeatable_group': {
      const min = field.minEntries ?? 0;
      return min > 0 && (!Array.isArray(value) || value.length < min);
    }
    default:
      return !value;
  }
}

function countMissingRequiredFields(step: IFormStep, values: Record<string, unknown>): number {
  let count = 0;
  for (const field of step.fields) {
    if (!isFieldVisible(field, values)) continue;
    if (isRequiredFieldMissing(field, values[field.key])) {
      count++;
      continue;
    }
    if (field.fieldType === 'repeatable_group' && field.groupFields) {
      const entries = Array.isArray(values[field.key])
        ? (values[field.key] as IRepeatableGroupEntry[]) // AS_JUSTIFIED: narrowed by Array.isArray check; repeatable_group field values are always IRepeatableGroupEntry[]
        : [];
      for (const entry of entries) {
        for (const sub of field.groupFields) {
          if (!isFieldVisible(sub, entry.values)) continue;
          if (isRequiredFieldMissing(sub, entry.values[sub.key])) count++;
        }
      }
    }
    if (field.fieldType === 'conditional_detail' && field.subFields) {
      const cdRaw = values[field.key];
      if (cdRaw && typeof cdRaw === 'object' && 'answer' in cdRaw) {
        const cd = cdRaw as IConditionalDetailValue; // AS_JUSTIFIED: runtime narrowed by 'answer' key check; matches conditional_detail field type
        if (cd.answer === true) {
          for (const sub of field.subFields) {
            if (isRequiredFieldMissing(sub, cd.subFieldValues?.[sub.key])) count++;
          }
        }
      }
    }
  }
  return count;
}

function countMissingPolicyInfoFields(
  policyInfoFields: IFormField[],
  values: Record<string, unknown>
): number {
  let count = 0;
  for (const field of policyInfoFields) {
    if (!isFieldVisible(field, values)) continue;
    if (isRequiredFieldMissing(field, values[field.key])) count++;
  }
  return count;
}

function buildReviewSections(
  templateSteps: IFormStep[],
  values: Record<string, unknown>,
  providerName: string,
  policyNumber: string,
  insuranceTypeName: string,
  missingByStep: number[],
  policyInfoFields: IFormField[]
): IReviewSectionItem[] {
  const policySetupMissing = countMissingPolicyInfoFields(policyInfoFields, values);
  const sections: IReviewSectionItem[] = [
    {
      key: 'policy-setup',
      title: 'Policy Setup',
      summary: `${insuranceTypeName} • ${providerName} • ${policyNumber || 'No policy number'}`,
      icon: 'policy-setup',
      stepIndex: POLICY_SETUP_STEP,
      hasError: policySetupMissing > 0,
      errorMessage:
        policySetupMissing > 0
          ? `${policySetupMissing} required field${policySetupMissing !== 1 ? 's' : ''} missing`
          : undefined,
    },
  ];

  for (let i = 0; i < templateSteps.length; i++) {
    const step = templateSteps[i];
    const stepIndex = i + 1;
    const sectionKey = step.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');

    let summary = '';
    let icon = 'policy-setup';

    const title = step.title.toLowerCase();
    if (title.includes('member')) {
      icon = 'member-details';
      const firstName = String(values.member_firstName ?? values.applicant_firstName ?? '');
      const lastName = String(values.member_lastName ?? values.applicant_lastName ?? '');
      const email = String(values.member_email ?? values.applicant_email ?? '');
      const phone = String(values.member_mobileNumber ?? values.applicant_mobileNumber ?? '');
      const parts = [firstName && lastName ? `${firstName} ${lastName}` : '', email, phone].filter(
        Boolean
      );
      summary = parts.join(' • ') || 'No details entered';
    } else if (title.includes('depend')) {
      icon = 'dependants';
      const deps = values.dependants as IRepeatableGroupEntry[] | undefined; // AS_JUSTIFIED: runtime type from form values matching repeatable_group field
      if (deps && deps.length > 0) {
        const names = deps
          .map(d => {
            const fn = String(d.values?.firstName ?? '');
            const ln = String(d.values?.lastName ?? '');
            const rel = String(d.values?.relationship ?? '');
            return fn && ln ? `${fn} ${ln}${rel ? ` (${rel})` : ''}` : null;
          })
          .filter(Boolean);
        summary = `${deps.length} dependant${deps.length !== 1 ? 's' : ''} added${names.length > 0 ? ` • ${names.join(', ')}` : ''}`;
      } else {
        summary = 'No dependants added';
      }
    } else if (title.includes('medical') || title.includes('insurance')) {
      icon = 'medical';
      const medicalFields = step.fields.filter(
        f => f.fieldType === 'conditional_detail' || f.fieldType === 'boolean'
      );
      const answered = medicalFields.filter(f => values[f.key] !== undefined).length;
      const hasYes = medicalFields.some(f => {
        const v = values[f.key];
        if (typeof v === 'boolean') return v;
        if (v && typeof v === 'object' && 'answer' in v)
          return (v as IConditionalDetailValue).answer; // AS_JUSTIFIED: runtime narrowed by 'answer' check
        return false;
      });
      summary = `${answered} question${answered !== 1 ? 's' : ''} answered • ${hasYes ? 'Conditions reported' : 'No conditions reported'}`;
    } else if (title.includes('personal information') || title.includes('privacy')) {
      icon = 'personal-info';
      summary = values.personalDataConsent ? 'Privacy consent provided' : 'Consent not provided';
    } else if (title.includes('declaration')) {
      icon = 'declaration';
      const signed = values.declarationConfirmed === true;
      const date = values.signatureDate ? String(values.signatureDate) : '';
      summary = signed ? `Declaration signed${date ? ` • ${date}` : ''}` : 'Not signed';
    } else if (title.includes('gdpr') || title.includes('data processing')) {
      icon = 'gdpr-consent';
      const consents = values.gdprConsents as IRepeatableGroupEntry[] | undefined; // AS_JUSTIFIED: runtime type from form values matching repeatable_group field
      const marketing = values.marketingConsents as IRepeatableGroupEntry[] | undefined; // AS_JUSTIFIED: runtime type from form values matching repeatable_group field
      const consentCount = consents?.length ?? 0;
      const hasMarketing = (marketing?.length ?? 0) > 0;
      summary = `${consentCount} person${consentCount !== 1 ? 's' : ''} consented${hasMarketing ? ' • Marketing consent provided' : ''}`;
    } else if (title.includes('legal') || title.includes('consent')) {
      icon = 'legal-consent';
      summary = 'All consents provided';
    } else {
      summary = `${step.fields.length} field${step.fields.length !== 1 ? 's' : ''}`;
    }

    const missing = missingByStep[i] ?? 0;
    sections.push({
      key: sectionKey,
      title: step.title,
      summary,
      icon,
      stepIndex,
      hasError: missing > 0,
      errorMessage:
        missing > 0 ? `${missing} required field${missing !== 1 ? 's' : ''} missing` : undefined,
    });
  }

  return sections;
}

export function PolicyApplicationWizard({
  template,
  orgId,
  existingPolicyId,
  existingData,
  policySource,
  policyStatus,
  rejectionReason,
  leadId,
  companyEmployeeId,
  onBack,
  onComplete,
  policySetupProps,
}: IPolicyApplicationWizardProps) {
  const {
    values,
    goToStep: rawGoToStep,
    updateValue,
    saveDraft,
    submitForReview,
    isSaving,
    isSubmitting,
  } = usePolicyApplicationWizard({
    template,
    orgId,
    existingPolicyId,
    existingData,
    policySource,
    leadId,
    companyEmployeeId,
  });

  const providerBenefitPlans = useQuery(
    api['insurance/benefitPlans'].listByProvider,
    policySetupProps?.providerFieldValue && orgId
      ? {
          orgId,
          providerId: policySetupProps.providerFieldValue as Id<'insuranceProviders'>, // AS_JUSTIFIED: providerFieldValue comes from the provider selector which only stores valid insuranceProviders document IDs
        }
      : 'skip'
  );

  const effectivePolicyInfoFields = useMemo((): IFormField[] => {
    const base = template.policyInfoFields ?? [];
    if (providerBenefitPlans && providerBenefitPlans.length > 0) {
      const syntheticPlanField: IFormField = {
        key: 'plan',
        label: 'Plan',
        fieldType: 'select',
        required: true,
        options: providerBenefitPlans.map(p => ({ label: p.name, value: p.name })),
      };
      // Replace any template-defined 'plan' field with the DB-driven one
      return [syntheticPlanField, ...base.filter(f => f.key !== 'plan')];
    }
    return base;
  }, [template.policyInfoFields, providerBenefitPlans]);

  const reviewStepIndex = template.steps.length + 1;

  const [localDisplayStep, setLocalDisplayStep] = useState(
    existingData ? (existingData.currentStep ?? 0) + 1 : POLICY_SETUP_STEP
  );

  const isReadOnly = policyStatus === 'active' || policyStatus === 'under_review';
  const isEditable = !policyStatus || policyStatus === 'draft' || policyStatus === 'rejected';

  const isOnPolicySetupLocal = localDisplayStep === POLICY_SETUP_STEP;
  const isOnReviewLocal = localDisplayStep === reviewStepIndex;
  const currentTemplateStepLocal =
    !isOnPolicySetupLocal && !isOnReviewLocal ? template.steps[localDisplayStep - 1] : null;

  const progressSteps = useMemo(
    () => [
      { title: 'Policy Setup' },
      ...template.steps.map(s => ({ title: s.title })),
      { title: 'Review' },
    ],
    [template.steps]
  );

  const missingByStep = useMemo(
    () => template.steps.map(step => countMissingRequiredFields(step, values)),
    [template.steps, values]
  );

  const errorStepDisplayIndices = useMemo(
    () => missingByStep.flatMap((n, i) => (n > 0 ? [i + 1] : [])),
    [missingByStep]
  );

  const handleStepClick = (idx: number) => {
    setLocalDisplayStep(idx);
    if (idx > POLICY_SETUP_STEP && idx < reviewStepIndex) {
      rawGoToStep(idx - 1);
    }
  };

  const handleNext = () => {
    if (localDisplayStep < reviewStepIndex) {
      const nextStep = localDisplayStep + 1;
      setLocalDisplayStep(nextStep);
      if (nextStep > POLICY_SETUP_STEP && nextStep < reviewStepIndex) {
        rawGoToStep(nextStep - 1);
      }
    }
  };

  const handlePrev = () => {
    if (localDisplayStep > POLICY_SETUP_STEP) {
      const prevStep = localDisplayStep - 1;
      setLocalDisplayStep(prevStep);
      if (prevStep > POLICY_SETUP_STEP && prevStep < reviewStepIndex) {
        rawGoToStep(prevStep - 1);
      }
    } else if (onBack) {
      onBack();
    }
  };

  const handleSubmit = async () => {
    await submitForReview();
    onComplete?.();
  };

  const p = policySetupProps;

  return (
    <div className="space-y-0">
      {policyStatus === 'under_review' && (
        <div className="px-10 py-3">
          <Badge variant="secondary" className="text-sm">
            Under Review
          </Badge>
        </div>
      )}

      {policyStatus === 'rejected' && rejectionReason && (
        <div className="mx-10 mb-4 flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-4">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <div>
            <p className="font-medium text-destructive">Application Rejected</p>
            <p className="text-sm text-muted-foreground">{rejectionReason}</p>
          </div>
        </div>
      )}

      <StepProgress
        steps={progressSteps}
        currentStep={localDisplayStep}
        onStepClick={handleStepClick}
        errorSteps={isOnReviewLocal ? errorStepDisplayIndices : undefined}
        showAllLabels={false}
        fullWidth={false}
      />

      <div className="px-10 py-8 space-y-6">
        {isOnPolicySetupLocal && p && (
          <>
            <PolicyInformationCard
              isEditing={p.isEditing}
              isSubmitting={p.isSubmitting}
              typeList={p.typeList}
              selectedTypeId={p.selectedTypeId}
              onTypeChange={p.onTypeChange}
              selectedTypeName={p.selectedTypeName}
              providerList={p.providerList}
              providerFieldValue={p.providerFieldValue}
              onProviderChange={p.onProviderChange}
              policyNumber={p.policyNumber}
              onPolicyNumberChange={p.onPolicyNumberChange}
              policyStartDate={p.policyStartDate}
              onPolicyStartDateChange={p.onPolicyStartDateChange}
              policyExpireDate={p.policyExpireDate}
              onPolicyExpireDateChange={p.onPolicyExpireDateChange}
              policyDuration={p.policyDuration}
              onPolicyDurationChange={p.onPolicyDurationChange}
              employeePolicyForm={p.employeePolicyForm}
              onEmployeeFormChange={p.onEmployeeFormChange}
              policyInfoFields={effectivePolicyInfoFields}
              policyInfoValues={values}
              onPolicyInfoChange={updateValue}
            />
            <PolicyStatusCard
              isSubmitting={p.isSubmitting}
              status={p.status}
              onStatusChange={p.onStatusChange}
              invoiceStatus={p.invoiceStatus}
              onInvoiceStatusChange={p.onInvoiceStatusChange}
              premium={p.premium}
              onPremiumChange={p.onPremiumChange}
              partiallyPaidAmount={p.partiallyPaidAmount}
              onPartiallyPaidAmountChange={p.onPartiallyPaidAmountChange}
            />
          </>
        )}

        {isOnPolicySetupLocal && !p && (
          <Card className="overflow-hidden py-0 gap-0">
            <CardHeader className="bg-muted border-b py-4">
              <CardTitle className="text-base">Policy Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <FieldRenderer
                  field={{
                    key: 'policyStartDate',
                    label: 'Policy Start Date',
                    fieldType: 'date',
                    required: true,
                  }}
                  value={values['policyStartDate']}
                  onChange={updateValue}
                  allValues={values}
                />
                {effectivePolicyInfoFields.map(field => (
                  <FieldRenderer
                    key={field.key}
                    field={field}
                    value={values[field.key]}
                    onChange={updateValue}
                    allValues={values}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {currentTemplateStepLocal && (
          <>
            {groupFieldsBySections(currentTemplateStepLocal.fields).map((section, sIdx) => {
              const fieldNodes = section.fields.map(field => {
                const isFullWidth = [
                  'textarea',
                  'address',
                  'repeatable_group',
                  'conditional_detail',
                  'checkbox',
                ].includes(field.fieldType);
                return (
                  <div key={field.key} className={isFullWidth ? 'col-span-full' : undefined}>
                    <FieldRenderer
                      field={field}
                      value={values[field.key]}
                      onChange={updateValue}
                      allValues={values}
                      disabled={isReadOnly}
                    />
                  </div>
                );
              });

              // Unsectioned fields render without Card wrapper (e.g. Dependants step)
              if (section.name === null) {
                return (
                  <div key={sIdx}>
                    {section.description && (
                      <p className="mb-4 text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                        {section.description}
                      </p>
                    )}
                    {fieldNodes}
                  </div>
                );
              }

              return (
                <Card key={section.name} className="overflow-hidden py-0 gap-0">
                  <CardHeader className="bg-muted border-b py-4">
                    <CardTitle className="flex items-center gap-2 text-base">
                      {section.icon && SECTION_ICONS[section.icon] && (
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground">
                          {SECTION_ICONS[section.icon]}
                        </span>
                      )}
                      {section.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 flex flex-col gap-4">
                    {section.description && (
                      <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                        {section.description}
                      </p>
                    )}
                    <div
                      className={cn(
                        'grid grid-cols-1 gap-x-6 gap-y-4',
                        GRID_COLS_CLASS[section.columns] ?? 'md:grid-cols-2'
                      )}
                    >
                      {fieldNodes}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </>
        )}

        {isOnReviewLocal && (
          <ReviewStep
            sections={buildReviewSections(
              template.steps,
              values,
              p?.providerFieldValue ?? '',
              p?.policyNumber ?? '',
              p?.selectedTypeName ?? 'Health Insurance',
              missingByStep,
              effectivePolicyInfoFields
            )}
            onEditSection={handleStepClick}
            onSubmit={() => void handleSubmit()}
            onBack={handlePrev}
            isSubmitting={isSubmitting}
          />
        )}

        {!isOnReviewLocal && (
          <div className="flex justify-between gap-3">
            <div className="flex gap-2">
              {localDisplayStep === POLICY_SETUP_STEP && onBack ? (
                <Button type="button" variant="outline" onClick={onBack} className="gap-1.5">
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
              ) : localDisplayStep > POLICY_SETUP_STEP ? (
                <Button type="button" variant="outline" onClick={handlePrev} className="gap-1.5">
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </Button>
              ) : null}
            </div>
            <div className="flex gap-2">
              {isEditable && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void saveDraft()}
                  disabled={isSaving}
                  className="gap-1.5"
                >
                  <Save className="h-4 w-4" />
                  {isSaving ? 'Saving...' : 'Save Draft'}
                </Button>
              )}
              <Button type="button" onClick={handleNext} className="gap-1.5">
                {localDisplayStep === reviewStepIndex - 1
                  ? 'Next: Review'
                  : `Next: ${progressSteps[localDisplayStep + 1]?.title ?? 'Next'}`}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
